import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RupiahInput from "@/components/RupiahInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Combobox from "@/components/Combobox";
import { toast } from "sonner";
import { Plus, ArrowLeft } from "@phosphor-icons/react";
import { computeSchedulePreview } from "@/lib/schedule";

export default function SaleForm() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [cars, setCars] = useState([]);
  const [rates, setRates] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", address: "", id_number: "" });
  const [newCarOpen, setNewCarOpen] = useState(false);
  const [newCar, setNewCar] = useState({ brand: "", model: "", type: "", year: new Date().getFullYear(), plate_number: "", color: "", price: "", account_number: "" });
  const [busy, setBusy] = useState(false);

  const initForm = {
    customer_id: "",
    car_id: "",
    sale_type: "cash",
    sale_date: new Date().toISOString().slice(0, 10),
    total_price: "",
    down_payment: "0",
    tenor_months: "12",
    payment_period_months: "1",
    interest_rate_id: "",
    account_number: "",
    makelar_name: "",
    makelar_commission: "0",
    custom_installment_on: false,
    custom_installment_amount: "",
    dp_cicil_on: false,
    dp_schedule: [],  // array of {due_date, amount}
  };
  const [form, setForm] = useState(initForm);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/cars").then((r) => setCars(r.data));
    api.get("/interest-rates").then((r) => setRates(r.data));
    api.get("/car-catalog").then((r) => setCatalog(r.data.grouped || {}));
  }, []);

  const selectedRate = rates.find((r) => r.id === form.interest_rate_id);
  const preview = useMemo(() => computeSchedulePreview({
    total_price: form.total_price,
    down_payment: form.down_payment,
    tenor_months: form.tenor_months,
    payment_period_months: form.payment_period_months,
    annual_rate: selectedRate?.annual_rate || 0,
    sale_date: form.sale_date,
    custom_installment_amount: form.custom_installment_on ? form.custom_installment_amount : 0,
  }), [form.total_price, form.down_payment, form.tenor_months, form.payment_period_months, selectedRate, form.sale_date, form.custom_installment_on, form.custom_installment_amount]);

  const availableCars = cars.filter((c) => c.status === "available");

  const setCar = (v) => {
    const c = cars.find((x) => x.id === v);
    setForm({
      ...form,
      car_id: v,
      total_price: c ? String(c.price) : form.total_price,
      account_number: c && c.account_number ? c.account_number : form.account_number,
    });
  };

  const submit = async () => {
    if (!form.customer_id) { toast.error("Pilih pelanggan terlebih dahulu"); return; }
    if (!form.car_id) { toast.error("Pilih mobil terlebih dahulu"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        car_id: form.car_id,
        sale_type: form.sale_type,
        sale_date: form.sale_date,
        total_price: parseFloat(form.total_price || 0),
        account_number: (form.account_number || "").trim() || null,
        makelar_name: (form.makelar_name || "").trim(),
        makelar_commission: parseFloat(form.makelar_commission || 0),
      };
      if (form.sale_type === "credit") {
        payload.down_payment = parseFloat(form.down_payment || 0);
        payload.tenor_months = parseInt(form.tenor_months);
        payload.payment_period_months = parseInt(form.payment_period_months);
        payload.interest_rate_id = form.interest_rate_id || null;
        if (form.custom_installment_on && parseFloat(form.custom_installment_amount || 0) > 0) {
          payload.custom_installment_amount = parseFloat(form.custom_installment_amount);
        }
        if (form.dp_cicil_on && Array.isArray(form.dp_schedule) && form.dp_schedule.length > 0) {
          payload.dp_schedule = form.dp_schedule
            .filter((x) => x.due_date && parseFloat(x.amount || 0) > 0)
            .map((x, i) => ({ sequence: i + 1, due_date: x.due_date, amount: parseFloat(x.amount) }));
        }
      }
      const r = await api.post("/sales", payload);
      toast.success("Transaksi berhasil dibuat");
      nav(`/sales/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat transaksi");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Transaksi Baru"
        subtitle="Isi detail penjualan cash atau kredit — sistem akan hitung jadwal cicilan otomatis"
        actions={
          <Link to="/sales">
            <Button data-testid="back-to-sales" className="rounded-none bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white uppercase tracking-wider text-xs h-10">
              <ArrowLeft size={14} className="mr-2" /> Kembali
            </Button>
          </Link>
        }
      />

      <div className="p-4 md:p-6 lg:p-10 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-200 p-6 bg-white">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider">No. Rekening <span className="text-gray-400 normal-case">(opsional — kosongkan untuk auto)</span></Label>
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="Contoh: 1501" className="mt-2 rounded-none font-mono-num" data-testid="sale-account-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Petugas / Makelar <span className="text-gray-400 normal-case">(kosongkan jika tanpa makelar)</span></Label>
            <Input value={form.makelar_name} onChange={(e) => setForm({ ...form, makelar_name: e.target.value })} placeholder="Contoh: Budi" className="mt-2 rounded-none" data-testid="sale-petugas-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Komisi Makelar</Label>
            <RupiahInput value={form.makelar_commission} onChange={(e) => setForm({ ...form, makelar_commission: e.target.value })} className="mt-2 rounded-none" data-testid="sale-commission-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Tipe</Label>
            <Select value={form.sale_type} onValueChange={(v) => setForm({ ...form, sale_type: v })}>
              <SelectTrigger data-testid="sale-type-trigger" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="credit">Kredit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Pelanggan</Label>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 min-w-0">
                <Combobox
                  value={form.customer_id}
                  onChange={(v) => setForm({ ...form, customer_id: v })}
                  options={customers.map((c) => ({
                    value: c.id,
                    label: `${c.blacklist ? "⚠ " : ""}${c.name}${c.phone ? " · " + c.phone : ""}`,
                    keywords: `${c.name} ${c.phone || ""} ${c.id_number || ""}`,
                  }))}
                  placeholder="Pilih pelanggan"
                  searchPlaceholder="Ketik nama / telepon / KTP..."
                  emptyText="Pelanggan tidak ditemukan"
                  testid="sale-customer-combobox"
                />
                {(() => {
                  const c = customers.find((x) => x.id === form.customer_id);
                  if (c?.blacklist) {
                    return (
                      <div className="mt-2 border-l-4 border-[#E63946] bg-red-50 p-3 text-xs" data-testid="blacklist-warning">
                        <div className="font-semibold text-[#E63946] uppercase tracking-wider">⚠ Pelanggan Blacklist</div>
                        <div className="text-gray-700 mt-1">{c.blacklist_reason || "Tidak ada alasan tercatat"}. Transaksi akan ditolak sistem.</div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              <button
                type="button"
                onClick={() => { setNewCust({ name: "", phone: "", address: "", id_number: "" }); setNewCustOpen(true); }}
                data-testid="new-customer-inline-btn"
                title="Tambah pelanggan baru"
                className="h-10 px-3 border border-gray-900 hover:bg-gray-900 hover:text-white transition-colors text-xs uppercase tracking-wider flex items-center gap-1"
              >
                <Plus size={14} /> Baru
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Mobil</Label>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 min-w-0">
                <Combobox
                  value={form.car_id}
                  onChange={setCar}
                  options={availableCars.map((c) => ({
                    value: c.id,
                    label: `${c.account_number ? c.account_number + " · " : ""}${c.brand} ${c.model}${c.type ? " · " + c.type : ""} · ${c.year}${c.plate_number ? " · " + c.plate_number : ""}`,
                    keywords: `${c.account_number || ""} ${c.brand} ${c.model} ${c.type || ""} ${c.year} ${c.plate_number || ""} ${c.color || ""}`,
                  }))}
                  placeholder="Pilih mobil"
                  searchPlaceholder="Ketik no. rek / merek / plat..."
                  emptyText="Mobil tidak ditemukan"
                  testid="sale-car-combobox"
                />
              </div>
              <button
                type="button"
                onClick={() => { setNewCar({ brand: "", model: "", type: "", year: new Date().getFullYear(), plate_number: "", color: "", price: "", account_number: "" }); setNewCarOpen(true); }}
                data-testid="new-car-inline-btn"
                title="Tambah mobil baru"
                className="h-10 px-3 border border-gray-900 hover:bg-gray-900 hover:text-white transition-colors text-xs uppercase tracking-wider flex items-center gap-1"
              >
                <Plus size={14} /> Baru
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Tanggal</Label>
            <Input type="date" value={form.sale_date} onChange={(e) => setForm({ ...form, sale_date: e.target.value })} className="mt-2 rounded-none" data-testid="sale-date-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Harga Jual</Label>
            <RupiahInput value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} className="mt-2 rounded-none" data-testid="sale-price-input" />
          </div>
          {form.sale_type === "credit" && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider">DP</Label>
                <RupiahInput value={form.down_payment} onChange={(e) => setForm({ ...form, down_payment: e.target.value })} className="mt-2 rounded-none" data-testid="sale-dp-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tenor (bulan)</Label>
                <Input type="number" min="1" max="48" value={form.tenor_months} onChange={(e) => setForm({ ...form, tenor_months: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="sale-tenor-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Periode Bayar</Label>
                <Select value={form.payment_period_months} onValueChange={(v) => setForm({ ...form, payment_period_months: v })}>
                  <SelectTrigger data-testid="sale-period-trigger" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Setiap Bulan</SelectItem>
                    <SelectItem value="4">Setiap 4 Bulan</SelectItem>
                    <SelectItem value="6">Setiap 6 Bulan</SelectItem>
                    <SelectItem value="12">Setiap Tahun</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Bunga</Label>
                <Select value={form.interest_rate_id} onValueChange={(v) => setForm({ ...form, interest_rate_id: v })}>
                  <SelectTrigger data-testid="sale-rate-trigger" className="mt-2 rounded-none"><SelectValue placeholder="Pilih bunga" /></SelectTrigger>
                  <SelectContent>
                    {rates.map((r) => (<SelectItem key={r.id} value={r.id}>{r.code} — {r.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              {form.total_price && (
                <div className="md:col-span-2 border border-gray-200 bg-gray-50 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">Perkiraan Cicilan</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Pokok Pinjaman</div>
                      <div className="font-mono-num font-semibold text-sm mt-1">{formatIDR(preview.principal)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Bunga Total</div>
                      <div className="font-mono-num font-semibold text-sm mt-1">{formatIDR(preview.totalInterest)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Per Angsuran</div>
                      <div className="font-mono-num font-bold text-sm mt-1 text-[#F97316]" data-testid="preview-per-payment">{formatIDR(preview.perPayment)}</div>
                      <div className="text-[10px] text-gray-500 font-mono-num">× {preview.nPayments} kali</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Total (DP + Angsuran)</div>
                      <div className="font-mono-num font-bold text-sm mt-1" data-testid="preview-grand-total">{formatIDR(preview.grandTotal)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Nego Cicilan (custom per-payment) */}
              {form.total_price && (
                <div className="md:col-span-2 border border-gray-200 bg-white">
                  <label className="flex items-center gap-3 p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={form.dp_cicil_on}
                      onChange={(e) => setForm({ ...form, dp_cicil_on: e.target.checked, dp_schedule: e.target.checked ? [{ due_date: form.sale_date, amount: "" }] : [] })}
                      data-testid="dp-cicil-toggle"
                      className="w-4 h-4 accent-[#2563EB]"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">Cicil DP (Uang Muka Dibayar Bertahap)</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Aktifkan jika DP dibayar lebih dari 1x. Isi jadwal janji bayar tiap termin di bawah.
                      </div>
                    </div>
                  </label>
                  {form.dp_cicil_on && (
                    <div className="p-4 space-y-2">
                      {(form.dp_schedule || []).map((item, idx) => (
                        <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:items-center border md:border-0 border-gray-100 p-2 md:p-0" data-testid={`dp-inst-${idx}`}>
                          <div className="md:col-span-1 font-mono-num font-semibold md:text-center">#{idx + 1}</div>
                          <Input type="date" value={item.due_date || ""} onChange={(e) => {
                            const s = [...form.dp_schedule]; s[idx] = { ...s[idx], due_date: e.target.value }; setForm({ ...form, dp_schedule: s });
                          }} className="md:col-span-5 rounded-none" />
                          <div className="md:col-span-5">
                            <RupiahInput value={item.amount || ""} onChange={(e) => {
                              const s = [...form.dp_schedule]; s[idx] = { ...s[idx], amount: e.target.value }; setForm({ ...form, dp_schedule: s });
                            }} className="rounded-none" />
                          </div>
                          <button
                            type="button"
                            onClick={() => { const s = form.dp_schedule.filter((_, i) => i !== idx); setForm({ ...form, dp_schedule: s }); }}
                            className="md:col-span-1 h-10 text-[#E63946] hover:bg-red-50 md:justify-self-end"
                          >× Hapus</button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, dp_schedule: [...(form.dp_schedule || []), { due_date: form.sale_date, amount: "" }] })}
                        data-testid="dp-add-inst"
                        className="text-xs uppercase tracking-wider border border-gray-900 px-3 py-2 hover:bg-gray-900 hover:text-white transition-colors mt-2"
                      >
                        + Tambah Termin DP
                      </button>
                      {(() => {
                        const total = (form.dp_schedule || []).reduce((a, b) => a + (parseFloat(b.amount) || 0), 0);
                        const dp = parseFloat(form.down_payment || 0);
                        const diff = dp - total;
                        return (
                          <div className={`mt-3 border-l-4 p-3 text-xs ${Math.abs(diff) < 0.01 ? "border-emerald-500 bg-emerald-50" : "border-yellow-500 bg-yellow-50"}`} data-testid="dp-sum-info">
                            <div className="font-semibold font-mono-num">Total jadwal: {formatIDR(total)} / DP: {formatIDR(dp)}</div>
                            {Math.abs(diff) >= 0.01 && (
                              <div className="text-gray-700 mt-1">Selisih {formatIDR(Math.abs(diff))} — {diff > 0 ? "kurang" : "lebih"} dari nilai DP.</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Nego Cicilan (custom per-payment) */}
              {form.total_price && (
                <div className="md:col-span-2 border border-gray-200 bg-white">
                  <label className="flex items-center gap-3 p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={form.custom_installment_on}
                      onChange={(e) => setForm({ ...form, custom_installment_on: e.target.checked })}
                      data-testid="custom-cicilan-toggle"
                      className="w-4 h-4 accent-[#F97316]"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">Cicilan Manual (Nego Pelanggan)</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Aktifkan jika pelanggan menawar cicilan lebih rendah. Selisih akan dicatat sebagai diskon otomatis.
                      </div>
                    </div>
                  </label>
                  {form.custom_installment_on && (
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs uppercase tracking-wider">Cicilan Kalkulator</Label>
                          <div className="mt-2 h-10 flex items-center border border-gray-200 bg-gray-50 px-3 font-mono-num text-sm" data-testid="calc-per-payment">
                            {formatIDR(preview.calculatedPerPayment)}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1 font-mono-num">Sesuai bunga {selectedRate?.annual_rate || 0}%</div>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider">Cicilan Nego (Manual)</Label>
                          <RupiahInput
                            value={form.custom_installment_amount}
                            onChange={(e) => setForm({ ...form, custom_installment_amount: e.target.value })}
                            className="mt-2 rounded-none"
                            data-testid="custom-cicilan-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider">Diskon Per Angsuran</Label>
                          <div
                            className={`mt-2 h-10 flex items-center border px-3 font-mono-num font-bold text-sm ${preview.discountPerPayment > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : preview.discountPerPayment < 0 ? "border-red-300 bg-red-50 text-red-700" : "border-gray-200 bg-gray-50"}`}
                            data-testid="discount-per-payment"
                          >
                            {formatIDR(preview.discountPerPayment)}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1 font-mono-num">
                            {preview.discountPerPayment > 0 ? "Turun dari kalkulator" : preview.discountPerPayment < 0 ? "Lebih tinggi dari kalkulator" : "Sama dengan kalkulator"}
                          </div>
                        </div>
                      </div>
                      {preview.totalDiscount !== 0 && (
                        <div className={`mt-3 border-l-4 p-3 text-sm ${preview.totalDiscount > 0 ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`} data-testid="total-discount-banner">
                          <div className="font-semibold">
                            {preview.totalDiscount > 0 ? "Total Diskon" : "Selisih Tambahan"}: <span className="font-mono-num" data-testid="total-discount">{formatIDR(Math.abs(preview.totalDiscount))}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1 font-mono-num">
                            Kalkulator: {formatIDR(preview.originalTotalRepay)} → Nego: {formatIDR(preview.totalRepay)} (× {preview.nPayments} angsuran)
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Full schedule preview */}
              {form.total_price && preview.schedule.length > 0 && (
                <div className="md:col-span-2 border border-gray-200 bg-white" data-testid="schedule-preview">
                  <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                        Jadwal Angsuran {preview.isCustom && <span className="ml-1 text-[#F97316]">· Cicilan Nego</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 font-mono-num">
                        {preview.nPayments} angsuran · Setiap {form.payment_period_months} bulan · Mulai {formatDate(preview.schedule[0].due_date)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">Angsuran Terakhir</div>
                      <div className="font-mono-num font-semibold text-sm mt-1">{formatDate(preview.schedule[preview.schedule.length - 1].due_date)}</div>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-gray-200 sticky top-0">
                        <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                          <th className="p-3 pl-4">Angs. #</th>
                          <th className="p-3">Jatuh Tempo</th>
                          <th className="p-3 text-right pr-4">Jumlah</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.schedule.map((row) => (
                          <tr key={row.sequence} className="border-b border-gray-50" data-testid={`preview-inst-${row.sequence}`}>
                            <td className="p-3 pl-4 font-mono-num font-semibold">#{row.sequence}</td>
                            <td className="p-3 font-mono-num text-xs">{formatDate(row.due_date)}</td>
                            <td className="p-3 text-right pr-4 font-mono-num font-semibold">{formatIDR(row.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-900 bg-yellow-50">
                          <td className="p-3 pl-4 font-bold uppercase text-xs" colSpan="2">Total Angsuran</td>
                          <td className="p-3 text-right pr-4 font-mono-num font-bold" data-testid="preview-schedule-total">{formatIDR(preview.totalRepay)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          {form.sale_type === "cash" && form.total_price && (
            <div className="md:col-span-2 border border-gray-200 bg-gray-50 p-4">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-2">Total Bayar</div>
              <div className="font-mono-num font-bold text-xl">{formatIDR(parseFloat(form.total_price || 0))}</div>
            </div>
          )}
          <div className="md:col-span-2 flex gap-3 pt-2">
            <Link to="/sales" className="flex-1">
              <Button type="button" className="w-full rounded-none bg-white border border-gray-300 hover:bg-gray-100 text-gray-900 uppercase tracking-wider text-xs h-11">
                Batal
              </Button>
            </Link>
            <Button onClick={submit} disabled={busy} data-testid="sale-submit" className="flex-1 rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">
              {busy ? "Menyimpan..." : "Simpan Transaksi"}
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog: Tambah Pelanggan Baru */}
      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">Pelanggan Baru</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label className="text-xs uppercase tracking-wider">Nama</Label><Input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} className="mt-2 rounded-none" data-testid="new-cust-name" autoFocus /></div>
            <div><Label className="text-xs uppercase tracking-wider">Telepon</Label><Input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="new-cust-phone" /></div>
            <div><Label className="text-xs uppercase tracking-wider">No. KTP</Label><Input value={newCust.id_number} onChange={(e) => setNewCust({ ...newCust, id_number: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Alamat</Label><Input value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} className="mt-2 rounded-none" /></div>
            <Button
              onClick={async () => {
                if (!newCust.name.trim()) { toast.error("Nama wajib"); return; }
                try {
                  const r = await api.post("/customers", newCust);
                  toast.success("Pelanggan ditambahkan");
                  const list = await api.get("/customers").then((x) => x.data);
                  setCustomers(list);
                  setForm((f) => ({ ...f, customer_id: r.data.id }));
                  setNewCustOpen(false);
                } catch (e) { toast.error("Gagal menambahkan"); }
              }}
              data-testid="new-cust-submit"
              className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11"
            >
              Simpan & Pilih
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Tambah Mobil Baru */}
      <Dialog open={newCarOpen} onOpenChange={setNewCarOpen}>
        <DialogContent className="rounded-none max-w-xl max-w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">Mobil Baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">No. Rekening <span className="text-gray-400 normal-case">(manual)</span></Label>
              <Input value={newCar.account_number} onChange={(e) => setNewCar({ ...newCar, account_number: e.target.value })} placeholder="Contoh: 1502" className="mt-2 rounded-none font-mono-num" data-testid="new-car-account" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Merek</Label>
              <Input list="nc-brands" value={newCar.brand} onChange={(e) => setNewCar({ ...newCar, brand: e.target.value, model: "", type: "" })} className="mt-2 rounded-none" data-testid="new-car-brand" />
              <datalist id="nc-brands">{Object.keys(catalog).sort().map((b) => <option key={b} value={b} />)}</datalist>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Model</Label>
              <Input list="nc-models" value={newCar.model} onChange={(e) => setNewCar({ ...newCar, model: e.target.value, type: "" })} className="mt-2 rounded-none" data-testid="new-car-model" />
              <datalist id="nc-models">{(catalog[newCar.brand] ? Object.keys(catalog[newCar.brand]) : []).map((m) => <option key={m} value={m} />)}</datalist>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Type / Varian</Label>
              <Input list="nc-types" value={newCar.type} onChange={(e) => setNewCar({ ...newCar, type: e.target.value })} placeholder="Contoh: Veloz 1.5 AT" className="mt-2 rounded-none" data-testid="new-car-type" />
              <datalist id="nc-types">{((catalog[newCar.brand] && catalog[newCar.brand][newCar.model]) || []).map((t) => <option key={t} value={t} />)}</datalist>
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Tahun</Label><Input type="number" value={newCar.year} onChange={(e) => setNewCar({ ...newCar, year: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Plat</Label><Input value={newCar.plate_number} onChange={(e) => setNewCar({ ...newCar, plate_number: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Warna</Label><Input value={newCar.color} onChange={(e) => setNewCar({ ...newCar, color: e.target.value })} className="mt-2 rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Harga</Label><RupiahInput value={newCar.price} onChange={(e) => setNewCar({ ...newCar, price: e.target.value })} className="mt-2 rounded-none" data-testid="new-car-price" /></div>
          </div>
          <Button
            onClick={async () => {
              if (!newCar.brand.trim() || !newCar.model.trim()) { toast.error("Merek & model wajib"); return; }
              try {
                const payload = { ...newCar, year: parseInt(newCar.year), price: parseFloat(newCar.price || 0) };
                const r = await api.post("/cars", payload);
                toast.success("Mobil ditambahkan ke stok");
                const list = await api.get("/cars").then((x) => x.data);
                setCars(list);
                setForm((f) => ({
                  ...f,
                  car_id: r.data.id,
                  total_price: String(r.data.price ?? payload.price ?? ""),
                  account_number: r.data.account_number || f.account_number,
                }));
                setNewCarOpen(false);
              } catch (e) { toast.error("Gagal menambahkan"); }
            }}
            data-testid="new-car-submit"
            className="mt-4 w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11"
          >
            Simpan & Pilih
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
